import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { designTokens } from '../tokens';

export type ListDetailWorkspaceProps = {
  children: ReactNode;
  detail: ReactNode;
  open: boolean;
  selectionKey?: string;
  layout?: 'split' | 'replace';
};

/** 목록의 검색·스크롤을 보존하며 넓은 화면에서는 비모달 상세를 나란히 배치합니다. */
export function ListDetailWorkspace({ children, detail, open, selectionKey, layout = 'split' }: ListDetailWorkspaceProps) {
  const wide = useMediaQuery(`(min-width:${designTokens.layout.listDetailMinViewport}px)`);
  const split = wide && layout === 'split';
  const listRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  const wasSplit = useRef(split);
  const previousSelection = useRef(selectionKey);
  useLayoutEffect(() => {
    let restoreFrame: number | undefined;
    if (open) {
      const active = document.activeElement;
      if (active instanceof HTMLElement && listRef.current?.contains(active)) triggerRef.current = active;
      if (!wasOpen.current || previousSelection.current !== selectionKey || (wasSplit.current && !split && !detailRef.current?.contains(active))) {
        detailRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
      }
    } else if (wasOpen.current) {
      const trigger = triggerRef.current;
      // 숨김을 해제한 프레임을 그린 뒤 목록의 원래 요소로 돌아갑니다.
      restoreFrame = window.requestAnimationFrame(() => {
        restoreFrame = window.requestAnimationFrame(() => {
          if (trigger?.isConnected && listRef.current?.contains(trigger)) trigger.focus();
          else listRef.current?.focus();
        });
      });
    }
    wasOpen.current = open;
    wasSplit.current = split;
    previousSelection.current = selectionKey;
    return () => { if (restoreFrame !== undefined) window.cancelAnimationFrame(restoreFrame); };
  }, [open, selectionKey, split]);

  return <Box sx={{ position: 'relative', display: 'grid', gridTemplateColumns: open && split ? `minmax(0, 1fr) ${designTokens.layout.detailPanelWidth}px` : 'minmax(0, 1fr)', height: '100%', minHeight: 0, minWidth: 0 }}>
    <Box ref={listRef} tabIndex={-1} onFocusCapture={(event) => { if (event.target instanceof HTMLElement) triggerRef.current = event.target; }} inert={open && !split || undefined} aria-hidden={open && !split || undefined} sx={{ display: 'flex', position: open && !split ? 'absolute' : 'relative', inset: open && !split ? 0 : 'auto', visibility: open && !split ? 'hidden' : 'visible', flexDirection: 'column', minWidth: 0, minHeight: 0, height: '100%' }}>{children}</Box>
    {open && <Box ref={detailRef} sx={{ minWidth: 0, minHeight: 0, height: '100%' }}>{detail}</Box>}
  </Box>;
}
